import { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { http, formatApiError } from "../../lib/api";
import { recordLink } from "../../lib/notify";
import { useNavDraft } from "../../lib/navDraft";
import { safePath } from "../../lib/nav";
import { trUpper } from "../../lib/uppercase";
import { fmtTL } from "../../lib/format";
import { toast } from "../../lib/notify";
import { Pencil, Trash2, Plus, Save, ChevronDown, ChevronRight, Wallet, GripVertical } from "lucide-react";
import { ConfirmDelete } from "../../components/ConfirmDelete";
import { useAuth } from "../../context/AuthContext";

const cls = "w-full px-3 py-2 text-sm border border-slate-300 rounded-md bg-white focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none";
const emptyForm = { name: "", parent_id: "", status: "Aktif" };

export default function BudgetItemsPage() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({});
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null); // "new" | id
  const [parentLocked, setParentLocked] = useState(false);
  useNavDraft("butce-kalemi", () => (editing ? { editing, form, parentLocked } : null), (d) => { setEditing(d.editing); setForm(d.form); setParentLocked(!!d.parentLocked); });
  const [collapsed, setCollapsed] = useState({});
  const [delItem, setDelItem] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const returnTo = safePath(searchParams.get("don")); // maliyet girişinden gelindi: kaydedince / ESC ile oraya dön
  const autoOpened = useRef(false);

  const load = () => Promise.all([
    http.get("/budget-items").then((r) => setItems(r.data)),
    http.get("/budget-items/totals").then((r) => setTotals(r.data)),
  ]).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);
  // ?yeni=gider → PROJE GİDERLERİ altına yeni alt kalem formu açık gelsin, Kalem Adı odaklı
  useEffect(() => {
    if (searchParams.get("yeni") !== "gider" || autoOpened.current || !items.length) return;
    const parent = items.find((b) => !b.parent_id && trUpper(b.name || "") === "PROJE GİDERLERİ");
    if (!parent) return;
    autoOpened.current = true;
    setForm({ ...emptyForm, parent_id: parent.id }); setParentLocked(true); setEditing("new");
    setTimeout(() => document.querySelector('[data-testid="bi-field-name"]')?.focus(), 80);
  }, [items, searchParams]);

  const [bq, setBq] = useState("");
  const bMatch = (s) => !bq.trim() || (s || "").toLocaleLowerCase("tr").includes(bq.trim().toLocaleLowerCase("tr"));
  const parents = items.filter((b) => !b.parent_id && (bMatch(b.name) || items.some((c) => c.parent_id === b.id && bMatch(c.name))));
  const childrenOf = (pid) => items.filter((b) => b.parent_id === pid && bMatch(b.name));
  const childTotal = (id) => totals[id]?.customer_total || totals[id]?.subcontractor_total || 0;
  const parentTotal = (p) => childrenOf(p.id).reduce((s, c) => s + childTotal(c.id), 0);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, parent_id: form.parent_id || null };
      const saved = editing === "new" ? (await http.post("/budget-items", payload)).data : (await http.put(`/budget-items/${editing}`, payload)).data;
      if (returnTo && editing === "new") {
        toast.success(`"${saved?.name || form.name}" kalemi eklendi — maliyet girişine dönüldü, listede yerini aldı`);
        navigate(returnTo);
        return;
      }
      toast.success("Bütçe kalemi kaydedildi", { link: recordLink(editing === "new" ? saved?.id : editing) });
      const groupId = form.parent_id || (editing !== "new" ? editing : null);
      setEditing(null); setForm(emptyForm); setParentLocked(false);
      await load();
      if (groupId) requestAnimationFrame(() => document.querySelector(`[data-testid="bi-group-${groupId}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" }));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeItem = async () => {
    if (!delItem) return;
    try {
      await http.delete(`/budget-items/${delItem.id}`);
      toast.success(`"${delItem.name}" silindi — Silinenler menüsünden geri alabilirsiniz`);
      setDelItem(null); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openEdit = (b) => { setForm({ name: b.name, parent_id: b.parent_id || "", status: b.status }); setParentLocked(false); setEditing(b.id); };
  const openNewChild = (p) => { setForm({ ...emptyForm, parent_id: p.id }); setParentLocked(true); setEditing("new"); };
  const cancel = () => { if (returnTo && editing === "new") { navigate(returnTo); return; } setEditing(null); setForm(emptyForm); setParentLocked(false); };
  const inlineEditId = editing && editing !== "new" && items.find((b) => b.id === editing)?.parent_id ? editing : null;
  const formProps = { form, setForm, parents, parentLocked, onSubmit: submit, onCancel: cancel };

  const [drag, setDrag] = useState(null); // { id, parentId|null }
  const dragId = drag?.id || null;
  const canDrag = isAdmin && !bq.trim();
  const dropOn = async (parentId, targetId) => {
    if (!drag || drag.id === targetId || drag.parentId !== parentId) { setDrag(null); return; }
    const inGroup = (b) => (parentId ? b.parent_id === parentId : !b.parent_id);
    const ids = items.filter(inGroup).map((b) => b.id);
    const from = ids.indexOf(drag.id), to = ids.indexOf(targetId);
    if (from < 0 || to < 0) { setDrag(null); return; }
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setDrag(null);
    setItems((prev) => { const ordered = ids.map((id) => prev.find((b) => b.id === id)); let k = 0; return prev.map((b) => (inGroup(b) ? ordered[k++] : b)); });
    try { await http.patch("/budget-items/reorder", { parent_id: parentId, ids }); toast.success("Sıralama kaydedildi"); load(); }
    catch (err) { toast.error(formatApiError(err)); load(); }
  };
  const dragProps = (id, parentId) => canDrag ? {
    draggable: true, onDragStart: (e) => { e.stopPropagation(); setDrag({ id, parentId }); }, onDragEnd: () => setDrag(null),
    onDragOver: (e) => { if (drag && drag.parentId === parentId) e.preventDefault(); }, onDrop: (e) => { e.preventDefault(); e.stopPropagation(); dropOn(parentId, id); },
  } : {};

  return (
    <div className="space-y-6" data-testid="page-budget-items">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2"><Wallet className="w-6 h-6 text-amber-500" /> Bütçe Kalemleri</h1>
          <input value={bq} onChange={(e) => setBq(e.target.value)} placeholder="Kalem ara…" data-testid="budget-search" className="ml-auto px-3 py-1.5 text-xs border border-slate-300 rounded-md w-44 bg-white" />
          <p className="text-sm text-slate-500 mt-1">Ana bütçe kalemi → alt bütçe kalemi şeklinde iki seviyelidir. Veri girişi yalnızca alt kalemlere yapılır; ana kalem, altındaki kalemlerin toplamını gösterir.{isAdmin && " Ana ve alt kalemleri tutamaçtan sürükleyerek sıralayabilirsiniz — sıra, Bütçe, maliyet ve kalem listelerinde de aynı kullanılır."}</p>
        </div>
        <button onClick={() => { setForm(emptyForm); setParentLocked(false); setEditing("new"); }} data-testid="bi-add"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800">
          <Plus className="w-3.5 h-3.5" /> Yeni Ana Kalem
        </button>
      </div>

      {editing && !inlineEditId && <BudgetItemForm {...formProps} />}
      {returnTo && editing === "new" && <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5" data-testid="bi-return-hint" data-return-bar="1">Proje maliyet girişinden geldiniz — Kaydet'e basınca ya da <kbd className="px-1 rounded border border-amber-300 bg-white font-mono">ESC</kbd> ile aynı filtre ve girdiğiniz tutarlarla maliyet girişine dönersiniz.</div>}

      <div className="space-y-3">
        {parents.map((p) => {
          const kids = childrenOf(p.id);
          const isCollapsed = collapsed[p.id];
          return (
            <div key={p.id} className={`bg-white border border-slate-200 rounded-lg overflow-hidden scroll-mt-20 md:scroll-mt-4 ${dragId === p.id ? "opacity-40" : ""}`} data-testid={`bi-group-${p.id}`} data-flash-id={p.id}>
              <div className="flex items-center gap-2 px-3 py-2.5 bg-slate-50 border-b border-slate-200 flex-wrap" {...dragProps(p.id, null)} data-testid={`bi-group-head-${p.id}`}>
                {canDrag && <GripVertical className="w-4 h-4 text-slate-300 cursor-grab active:cursor-grabbing shrink-0" data-testid={`bi-drag-parent-${p.id}`} title="Ana kalemi sürükleyerek sırala" />}
                <button onClick={() => setCollapsed({ ...collapsed, [p.id]: !isCollapsed })} data-testid={`bi-toggle-${p.id}`} className="text-slate-500 hover:text-slate-800">
                  {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                <span className="text-sm font-bold text-slate-900">{p.name}</span>
                <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 text-[10px] font-semibold">{kids.length} alt kalem</span>
                {p.status !== "Aktif" && <span className="text-[10px] text-red-600 font-semibold">{p.status}</span>}
                <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-[11px] font-bold" data-testid={`bi-total-${p.id}`} title="Alt kalemlerin toplamı">{fmtTL(parentTotal(p))}</span>
                <div className="ml-auto flex items-center gap-1">
                  <button onClick={() => openNewChild(p)} data-testid={`bi-add-child-${p.id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-300 bg-white text-[11px] font-semibold text-slate-700 hover:bg-slate-50">
                    <Plus className="w-3 h-3" /> Alt Kalem Ekle
                  </button>
                  <button onClick={() => openEdit(p)} className="text-slate-600 hover:text-slate-900 px-1" data-testid={`bi-edit-${p.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                  {isAdmin && (
                    <button onClick={() => setDelItem(p)} className="text-red-700 hover:text-red-900 px-1" data-testid={`bi-delete-${p.id}`} title="Ana kalemi ve alt kalemlerini sil"><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </div>
              </div>
              {!isCollapsed && (
                <table className="w-full tbl">
                  <tbody>
                    {kids.map((c) => (
                      <Fragment key={c.id}>
                        <tr className={`border-t border-slate-100 hover:bg-slate-50/60 ${inlineEditId === c.id ? "bg-amber-50" : ""} ${dragId === c.id ? "opacity-40" : ""}`} data-testid={`bi-row-${c.id}`}
                          {...dragProps(c.id, p.id)}>
                          <td className="px-3 py-2 pl-4 text-xs font-medium">
                            <span className="inline-flex items-center gap-1.5">
                              {canDrag && <GripVertical className="w-3.5 h-3.5 text-slate-300 cursor-grab active:cursor-grabbing shrink-0" data-testid={`bi-drag-${c.id}`} title="Sürükleyerek sırala" />}
                              {c.name}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs w-24">{c.status}</td>
                          <td className="px-3 py-2 text-xs text-right w-40 font-semibold" data-testid={`bi-child-total-${c.id}`}>{fmtTL(childTotal(c.id))}</td>
                          <td className="px-3 py-2 text-right w-36 whitespace-nowrap">
                            <button onClick={() => openEdit(c)} className="text-slate-600 hover:text-slate-900 mr-2" data-testid={`bi-edit-${c.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                            {isAdmin && (
                              <button onClick={() => setDelItem(c)} className="text-red-700 hover:text-red-900" data-testid={`bi-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                            )}
                          </td>
                        </tr>
                        {inlineEditId === c.id && (
                          <tr data-testid={`bi-inline-edit-${c.id}`}>
                            <td colSpan={4} className="p-2 pl-9 bg-amber-50/60 border-t border-amber-200">
                              <BudgetItemForm {...formProps} inline />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                    {kids.length === 0 && <tr><td colSpan={4} className="py-4 text-center text-xs text-slate-400" data-testid={`bi-empty-${p.id}`}>Alt kalem yok — "Alt Kalem Ekle" ile açın. Veri girişi yalnızca alt kalemlere yapılır.</td></tr>}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
        {parents.length === 0 && (
          <div className="text-xs text-slate-500 py-3 bg-amber-50 border border-amber-200 rounded-md px-3" data-testid="bi-empty-all">
            Henüz bütçe kalemi yok. <span className="font-semibold">"Yeni Ana Kalem"</span> ile başlayın.
          </div>
        )}
      </div>

      {delItem && (
        <ConfirmDelete testid="bi-del" title={`Bütçe Kalemi Sil: ${delItem.name}`}
          message={delItem.parent_id
            ? `"${delItem.name}" alt kalemi silinecek — veriler çöp kutusuna taşınır. Emin misiniz?`
            : `"${delItem.name}" ana kalemi ve altındaki ${childrenOf(delItem.id).length} alt kalem birlikte silinecek — veriler çöp kutusuna taşınır. Emin misiniz?`}
          step2Message={`Son onay: "${delItem.name}" silinsin mi?`}
          onConfirm={removeItem} onCancel={() => setDelItem(null)} />
      )}
    </div>
  );
}

function BudgetItemForm({ form, setForm, parents, parentLocked, onSubmit, onCancel, inline }) {
  const nameRef = useRef(null);
  useEffect(() => {
    nameRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    nameRef.current?.focus();
  }, []);
  const wrap = inline ? "flex items-end gap-3 flex-wrap" : "bg-white border border-slate-200 rounded-lg p-4 flex items-end gap-3 flex-wrap";
  return (
    <form onSubmit={onSubmit} className={wrap} data-testid="bi-form">
      <L label="Kalem Adı"><input ref={nameRef} required data-testid="bi-field-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={cls} placeholder="örn. Personel Giderleri" /></L>
      <L label="Ana Bütçe Kalemi (boş = ana kalem olur)">
        <select data-testid="bi-field-parent" disabled={parentLocked} value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })} className={`${cls} disabled:bg-slate-50 disabled:text-slate-400`}>
          <option value="">— Ana kalem olarak aç —</option>
          {parents.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </L>
      <L label="Durum">
        <select data-testid="bi-field-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={cls}>
          <option value="Aktif">Aktif</option>
          <option value="Pasif">Pasif</option>
        </select>
      </L>
      <div className="flex gap-2">
        <button type="submit" data-testid="bi-save" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800"><Save className="w-3.5 h-3.5" /> Kaydet</button>
        <button type="button" onClick={onCancel} data-testid="bi-cancel" className="px-3 py-2 text-xs text-slate-600 hover:text-slate-900">İptal</button>
      </div>
    </form>
  );
}

function L({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide">{label}</span>
      {children}
    </label>
  );
}
